import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
    fetchDeletionQueue,
    permanentlyDeleteRequests,
    reverseDeletionRequests
} from '../attendedUtils';

const ENTITY_TYPES = ['Artist', 'Event', 'Festival', 'Member', 'Promoter', 'Venue'];
const PAGE_SIZE = 50;
const REASON_PREVIEW_LENGTH = 60;

// Format a UTC timestamp as MM/DD/YYYY HH:MM UTC — matches ChangeLog.js exactly
function formatTimestamp(ts) {
    if (!ts) return '—';
    const d = new Date(ts);
    const mm  = String(d.getUTCMonth() + 1).padStart(2, '0');
    const dd  = String(d.getUTCDate()).padStart(2, '0');
    const yyyy= d.getUTCFullYear();
    const hh  = String(d.getUTCHours()).padStart(2, '0');
    const min = String(d.getUTCMinutes()).padStart(2, '0');
    return `${mm}/${dd}/${yyyy} ${hh}:${min} UTC`;
}

// Format event_date (YYYY-MM-DD) as MM/DD/YYYY — matches ChangeLog.js exactly
function formatEventDate(dateStr) {
    if (!dateStr) return '';
    const datePart = String(dateStr).split('T')[0];
    const d = new Date(datePart + 'T00:00:00Z');
    if (isNaN(d.getTime())) return dateStr;
    return d.toLocaleDateString('en-US', { year: 'numeric', month: '2-digit', day: '2-digit', timeZone: 'UTC' });
}

// Compose the entity display string — same contract as ChangeLog.js's renderEntityName,
// since getDeletionQueue.js returns entity_display in the identical shape
function renderEntityName(row) {
    if (row.entity_type === 'event') {
        const d = row.entity_display || {};
        const parts = [
            d.event_date ? formatEventDate(d.event_date) : null,
            d.venue_name || null,
            d.headliner_name || null,
        ].filter(Boolean);
        return parts.length ? parts.join(' – ') : `Event #${row.entity_id}`;
    }
    return row.entity_display || `[Deleted ${row.entity_type}]`;
}

export default function DeletionQueue() {
    const [rows, setRows]       = useState([]);
    const [total, setTotal]     = useState(0);
    const [loading, setLoading] = useState(false);
    const [error, setError]     = useState(null);
    const [offset, setOffset]   = useState(0);
    const [jumpPage, setJumpPage] = useState('');

    // Filter state
    const [entityType, setEntityType] = useState('');
    const [search, setSearch]         = useState('');
    const [dateStart, setDateStart]   = useState('');
    const [dateEnd, setDateEnd]       = useState('');

    // Selection state
    const [selectedIds, setSelectedIds] = useState(new Set());
    const [expandedReasonId, setExpandedReasonId] = useState(null);

    // Bulk action state
    const [actionType, setActionType] = useState(null); // 'delete' | 'reverse' | null
    const [actionSubmitting, setActionSubmitting] = useState(false);
    const [actionResults, setActionResults] = useState(null); // { results: [...] } after completion

    // Debounce search input
    const searchTimerRef = useRef(null);
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const handleSearchChange = (e) => {
        const val = e.target.value;
        setSearch(val);
        clearTimeout(searchTimerRef.current);
        searchTimerRef.current = setTimeout(() => {
            setDebouncedSearch(val);
            setOffset(0);
        }, 300);
    };

    const fetchQueue = useCallback(async (currentOffset) => {
        setLoading(true);
        setError(null);
        try {
            const filters = { limit: PAGE_SIZE, offset: currentOffset };
            if (entityType)      filters.entity_type = entityType.toLowerCase();
            if (debouncedSearch) filters.search       = debouncedSearch;
            if (dateStart)       filters.date_start    = dateStart;
            if (dateEnd)         filters.date_end      = dateEnd;

            const data = await fetchDeletionQueue(filters);
            setRows(data.rows || []);
            setTotal(data.total || 0);
        } catch (err) {
            setError('Failed to load deletion queue. Please try again.');
        } finally {
            setLoading(false);
        }
    }, [entityType, debouncedSearch, dateStart, dateEnd]);

    useEffect(() => { fetchQueue(offset); }, [fetchQueue, offset]);

    useEffect(() => { setOffset(0); }, [entityType, debouncedSearch, dateStart, dateEnd]);

    // Clear selection whenever the visible page of rows changes
    useEffect(() => { setSelectedIds(new Set()); }, [rows]);

    const totalPages = Math.ceil(total / PAGE_SIZE);
    const currentPage = Math.floor(offset / PAGE_SIZE) + 1;

    const allOnPageSelected = rows.length > 0 && rows.every(r => selectedIds.has(r.id));

    const toggleSelectAll = () => {
        if (allOnPageSelected) {
            setSelectedIds(new Set());
        } else {
            setSelectedIds(new Set(rows.map(r => r.id)));
        }
    };

    const toggleSelectOne = (id) => {
        setSelectedIds(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    const selectedRows = rows.filter(r => selectedIds.has(r.id));

    const openBulkAction = (type) => {
        setActionType(type);
        setActionResults(null);
    };

    const closeBulkAction = () => {
        setActionType(null);
        setActionResults(null);
    };

    const handleConfirmAction = async () => {
        setActionSubmitting(true);
        try {
            const ids = Array.from(selectedIds);
            const data = actionType === 'delete'
                ? await permanentlyDeleteRequests(ids)
                : await reverseDeletionRequests(ids);
            setActionResults(data.results || []);
            setSelectedIds(new Set());
            fetchQueue(offset); // refresh list — resolved rows will drop off
        } catch (err) {
            setActionResults([{ id: null, success: false, error: err.response?.data?.error || 'Request failed.' }]);
        } finally {
            setActionSubmitting(false);
        }
    };

    const successCount = actionResults ? actionResults.filter(r => r.success).length : 0;
    const failureCount = actionResults ? actionResults.filter(r => !r.success).length : 0;

    return (
        <div style={S.page}>
            {/* Header */}
            <div style={S.header}>
                <h1 style={S.title}>Deletion Queue</h1>
                <p style={S.subtitle}>Review, permanently delete, or reverse pending deletion requests.</p>
            </div>

            {/* Filter bar */}
            <div style={S.filterBar}>
                <select
                    value={entityType}
                    onChange={e => { setEntityType(e.target.value); setOffset(0); }}
                    style={{ ...S.filterControl, flex: '0 0 10%' }}
                >
                    <option value=''>All Types</option>
                    {ENTITY_TYPES.map(t => (
                        <option key={t} value={t}>{t}</option>
                    ))}
                </select>

                <input
                    type='text'
                    placeholder='Search by entity name or reason…'
                    value={search}
                    onChange={handleSearchChange}
                    style={{ ...S.filterControl, flex: '0 0 50%' }}
                />

                <input
                    type='date'
                    value={dateStart}
                    onChange={e => { setDateStart(e.target.value); setOffset(0); }}
                    style={{ ...S.filterControl, flex: '0 0 20%', colorScheme: 'dark' }}
                />

                <input
                    type='date'
                    value={dateEnd}
                    onChange={e => { setDateEnd(e.target.value); setOffset(0); }}
                    style={{ ...S.filterControl, flex: '0 0 20%', colorScheme: 'dark' }}
                />
            </div>

            {/* Bulk action bar */}
            <div style={S.bulkBar}>
                <button
                    onClick={() => openBulkAction('delete')}
                    disabled={selectedIds.size === 0}
                    style={{ ...S.bulkDeleteBtn, opacity: selectedIds.size === 0 ? 0.4 : 1, cursor: selectedIds.size === 0 ? 'not-allowed' : 'pointer' }}
                >
                    Permanently Delete{selectedIds.size > 0 ? ` (${selectedIds.size})` : ''}
                </button>
                <button
                    onClick={() => openBulkAction('reverse')}
                    disabled={selectedIds.size === 0}
                    style={{ ...S.bulkReverseBtn, opacity: selectedIds.size === 0 ? 0.4 : 1, cursor: selectedIds.size === 0 ? 'not-allowed' : 'pointer' }}
                >
                    Reverse Deletion Request{selectedIds.size > 0 ? ` (${selectedIds.size})` : ''}
                </button>
            </div>

            {/* Status bar */}
            <div style={S.statusBar}>
                {loading ? (
                    <span style={S.statusText}>Loading…</span>
                ) : error ? (
                    <span style={{ ...S.statusText, color: '#e57373' }}>{error}</span>
                ) : (
                    <span style={S.statusText}>
                        {total.toLocaleString()} pending {total === 1 ? 'request' : 'requests'}
                        {totalPages > 1 && ` — page ${currentPage} of ${totalPages}`}
                    </span>
                )}
            </div>

            {/* Table */}
            {!loading && !error && rows.length === 0 ? (
                <div style={S.empty}>No pending deletion requests.</div>
            ) : (
                <div style={S.tableWrap}>
                    <table style={S.table}>
                        <thead>
                            <tr>
                                <th style={{ ...S.th, width: '36px' }}>
                                    <input
                                        type='checkbox'
                                        checked={allOnPageSelected}
                                        onChange={toggleSelectAll}
                                        style={S.checkbox}
                                    />
                                </th>
                                {['Object Type', 'Name', 'Reason', 'Deletion Requested', 'Requested By'].map(h => (
                                    <th key={h} style={S.th}>{h}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map(row => {
                                const isExpanded = expandedReasonId === row.id;
                                const reasonIsLong = (row.reason || '').length > REASON_PREVIEW_LENGTH;
                                const reasonPreview = reasonIsLong && !isExpanded
                                    ? `${row.reason.slice(0, REASON_PREVIEW_LENGTH)}…`
                                    : row.reason;
                                return (
                                    <tr key={row.id} style={S.tr}>
                                        <td style={S.td}>
                                            <input
                                                type='checkbox'
                                                checked={selectedIds.has(row.id)}
                                                onChange={() => toggleSelectOne(row.id)}
                                                style={S.checkbox}
                                            />
                                        </td>
                                        <td style={S.td}>{row.entity_type.charAt(0).toUpperCase() + row.entity_type.slice(1)}</td>
                                        <td style={S.td}>{renderEntityName(row)}</td>
                                        <td style={S.tdReason} title={row.reason}>
                                            {reasonPreview}
                                            {reasonIsLong && (
                                                <button
                                                    onClick={() => setExpandedReasonId(isExpanded ? null : row.id)}
                                                    style={S.expandBtn}
                                                >
                                                    {isExpanded ? 'Show less' : 'Show more'}
                                                </button>
                                            )}
                                        </td>
                                        <td style={{ ...S.td, whiteSpace: 'nowrap' }}>{formatTimestamp(row.requested_at)}</td>
                                        <td style={S.td}>{row.requested_by_display_name || row.requested_by}</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
                <div style={S.pagination}>
                    <button
                        onClick={() => { setOffset(0); setJumpPage(''); }}
                        disabled={offset === 0}
                        style={{ ...S.pageBtn, opacity: offset === 0 ? 0.4 : 1 }}
                    >
                        «
                    </button>
                    <button
                        onClick={() => { setOffset(Math.max(0, offset - PAGE_SIZE)); setJumpPage(''); }}
                        disabled={offset === 0}
                        style={{ ...S.pageBtn, opacity: offset === 0 ? 0.4 : 1 }}
                    >
                        ‹ Prev
                    </button>
                    <span style={S.pageInfo}>
                        Page {currentPage} of {totalPages}
                    </span>
                    {totalPages > 10 && (
                        <span style={S.jumpWrap}>
                            <span style={S.jumpLabel}>Go to</span>
                            <input
                                type='number'
                                min={1}
                                max={totalPages}
                                value={jumpPage}
                                onChange={e => setJumpPage(e.target.value)}
                                onKeyDown={e => {
                                    if (e.key === 'Enter') {
                                        const p = parseInt(jumpPage, 10);
                                        if (!isNaN(p) && p >= 1 && p <= totalPages) {
                                            setOffset((p - 1) * PAGE_SIZE);
                                            setJumpPage('');
                                        }
                                    }
                                }}
                                style={S.jumpInput}
                                placeholder='#'
                            />
                            <button
                                onClick={() => {
                                    const p = parseInt(jumpPage, 10);
                                    if (!isNaN(p) && p >= 1 && p <= totalPages) {
                                        setOffset((p - 1) * PAGE_SIZE);
                                        setJumpPage('');
                                    }
                                }}
                                style={S.pageBtn}
                            >
                                Go
                            </button>
                        </span>
                    )}
                    <button
                        onClick={() => { setOffset(offset + PAGE_SIZE); setJumpPage(''); }}
                        disabled={offset + PAGE_SIZE >= total}
                        style={{ ...S.pageBtn, opacity: offset + PAGE_SIZE >= total ? 0.4 : 1 }}
                    >
                        Next ›
                    </button>
                    <button
                        onClick={() => { setOffset((totalPages - 1) * PAGE_SIZE); setJumpPage(''); }}
                        disabled={offset + PAGE_SIZE >= total}
                        style={{ ...S.pageBtn, opacity: offset + PAGE_SIZE >= total ? 0.4 : 1 }}
                    >
                        »
                    </button>
                </div>
            )}

            {actionType && (
                <BulkActionModal
                    actionType={actionType}
                    selectedRows={selectedRows}
                    submitting={actionSubmitting}
                    results={actionResults}
                    successCount={successCount}
                    failureCount={failureCount}
                    onConfirm={handleConfirmAction}
                    onClose={closeBulkAction}
                />
            )}
        </div>
    );
}

// ── Bulk action confirmation modal ──────────────────────────────────────────
// Handles both "Permanently Delete" and "Reverse Deletion Request" flows.
// Lists each selected record's name AND reason (spec Section 3.3) so the
// admin gets one last look before committing. After submission, shows
// per-row success/failure (spec Section 11 — independent resolution).
function BulkActionModal({ actionType, selectedRows, submitting, results, successCount, failureCount, onConfirm, onClose }) {
    const isDelete = actionType === 'delete';
    const title = isDelete ? 'Permanently Delete?' : 'Reverse Deletion Request?';
    const warningText = isDelete
        ? 'This will permanently remove the following records from the database. This cannot be undone.'
        : `Restore ${selectedRows.length} record${selectedRows.length === 1 ? '' : 's'}? They will become visible again.`;

    return (
        <div style={S.modalOverlay} onClick={e => { if (e.target === e.currentTarget && !submitting) onClose(); }}>
            <div style={S.modal}>
                <div style={S.modalHeader}>
                    <span style={S.modalTitle}>{title}</span>
                    <button onClick={onClose} disabled={submitting} style={S.closeBtn}>✕</button>
                </div>

                {!results ? (
                    <>
                        <p style={S.modalBody}>{warningText}</p>

                        <div style={S.recordList}>
                            {selectedRows.map(row => (
                                <div key={row.id} style={S.recordItem}>
                                    <div style={S.recordName}>
                                        {row.entity_type.charAt(0).toUpperCase() + row.entity_type.slice(1)} — {renderEntityName(row)}
                                    </div>
                                    <div style={S.recordReason}>{row.reason}</div>
                                </div>
                            ))}
                        </div>

                        <div style={S.modalFooter}>
                            <button onClick={onClose} disabled={submitting} style={S.ghostBtn}>
                                Cancel
                            </button>
                            <button
                                onClick={onConfirm}
                                disabled={submitting}
                                style={isDelete ? S.confirmDeleteBtn : S.confirmReverseBtn}
                            >
                                {submitting ? 'Processing…' : isDelete ? 'Yes, Permanently Delete' : 'Yes, Reverse'}
                            </button>
                        </div>
                    </>
                ) : (
                    <>
                        <p style={S.modalBody}>
                            {successCount > 0 && <span style={S.resultSuccess}>{successCount} succeeded.</span>}
                            {failureCount > 0 && <span style={S.resultFailure}> {failureCount} failed.</span>}
                        </p>
                        {failureCount > 0 && (
                            <div style={S.recordList}>
                                {results.filter(r => !r.success).map((r, i) => (
                                    <div key={r.id ?? i} style={S.recordItem}>
                                        <div style={S.resultFailure}>{r.error || 'Unknown error'}</div>
                                    </div>
                                ))}
                            </div>
                        )}
                        <div style={S.modalFooter}>
                            <button onClick={onClose} style={S.ghostBtn}>Close</button>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}

// ── Styles ────────────────────────────────────────────────────────────────────
const S = {
    page: {
        maxWidth: '1400px',
        margin: '0 auto',
        padding: '40px 24px',
        color: '#e0e0e0',
        fontFamily: 'sans-serif',
    },
    header: { marginBottom: '28px' },
    title: { fontSize: '28px', fontWeight: 'bold', color: '#bb86fc', margin: '0 0 6px 0' },
    subtitle: { fontSize: '14px', color: '#777', margin: 0 },
    filterBar: { display: 'flex', gap: '8px', marginBottom: '16px', flexWrap: 'nowrap' },
    filterControl: {
        backgroundColor: '#2c2c2c', border: '1px solid #444', borderRadius: '4px',
        color: '#e0e0e0', fontSize: '13px', padding: '8px 10px', height: '36px',
        boxSizing: 'border-box', outline: 'none', minWidth: 0,
    },
    bulkBar: { display: 'flex', gap: '10px', marginBottom: '16px' },
    bulkDeleteBtn: {
        backgroundColor: '#2c1818', color: '#e57373', border: '1px solid #e57373',
        borderRadius: '4px', padding: '8px 16px', fontSize: '13px', fontWeight: 'bold',
    },
    bulkReverseBtn: {
        backgroundColor: '#1e1e2e', color: '#bb86fc', border: '1px solid #bb86fc',
        borderRadius: '4px', padding: '8px 16px', fontSize: '13px', fontWeight: 'bold',
    },
    statusBar: { marginBottom: '12px' },
    statusText: { fontSize: '13px', color: '#777' },
    empty: { padding: '48px', textAlign: 'center', color: '#555', fontSize: '14px' },
    tableWrap: { overflowX: 'auto', borderRadius: '8px', border: '1px solid #2a2a2a' },
    table: { width: '100%', borderCollapse: 'collapse', fontSize: '13px' },
    th: {
        backgroundColor: '#1e1e2e', color: '#bb86fc', fontSize: '11px', fontWeight: 'bold',
        textTransform: 'uppercase', letterSpacing: '0.8px', padding: '10px 12px',
        textAlign: 'left', borderBottom: '1px solid #333', whiteSpace: 'nowrap',
    },
    tr: { borderBottom: '1px solid #2a2a2a', transition: 'background-color 0.1s' },
    td: { padding: '10px 12px', color: '#e0e0e0', verticalAlign: 'top', fontSize: '13px' },
    tdReason: {
        padding: '10px 12px', color: '#ccc', verticalAlign: 'top', fontSize: '13px',
        maxWidth: '320px', wordBreak: 'break-word',
    },
    expandBtn: {
        background: 'transparent', border: 'none', color: '#bb86fc', fontSize: '12px',
        cursor: 'pointer', padding: '0 0 0 6px', textDecoration: 'underline',
    },
    checkbox: { width: '15px', height: '15px', cursor: 'pointer', accentColor: '#bb86fc' },
    pagination: { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', marginTop: '24px', flexWrap: 'nowrap' },
    pageBtn: {
        backgroundColor: '#2c2c2c', border: '1px solid #444', borderRadius: '4px',
        color: '#e0e0e0', fontSize: '13px', padding: '6px 12px', cursor: 'pointer', whiteSpace: 'nowrap',
    },
    pageInfo: { fontSize: '13px', color: '#aaa', whiteSpace: 'nowrap', padding: '0 8px' },
    jumpWrap: { display: 'flex', alignItems: 'center', gap: '6px', marginLeft: '8px' },
    jumpLabel: { fontSize: '13px', color: '#777', whiteSpace: 'nowrap' },
    jumpInput: {
        backgroundColor: '#2c2c2c', border: '1px solid #444', borderRadius: '4px',
        color: '#e0e0e0', fontSize: '13px', padding: '6px 8px', width: '60px',
        textAlign: 'center', outline: 'none',
    },

    // Modal
    modalOverlay: {
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 2000, padding: '16px',
    },
    modal: {
        background: '#1e1e1e', border: '1px solid #333', borderRadius: '12px',
        padding: '24px', width: '100%', maxWidth: '520px', maxHeight: '85vh', overflowY: 'auto',
    },
    modalHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' },
    modalTitle: { fontSize: '18px', fontWeight: 600, color: '#e0e0e0' },
    closeBtn: { background: 'transparent', border: 'none', color: '#888', fontSize: '18px', cursor: 'pointer', lineHeight: 1 },
    modalBody: { fontSize: '14px', color: '#bbb', lineHeight: 1.6, margin: '0 0 16px' },
    recordList: {
        display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '8px',
        maxHeight: '280px', overflowY: 'auto',
    },
    recordItem: {
        backgroundColor: '#141414', border: '1px solid #2a2a2a', borderRadius: '6px',
        padding: '10px 12px',
    },
    recordName: { fontSize: '13px', fontWeight: 600, color: '#e0e0e0', marginBottom: '4px' },
    recordReason: { fontSize: '12px', color: '#999', lineHeight: 1.5 },
    resultSuccess: { color: '#81c784', fontSize: '14px' },
    resultFailure: { color: '#e57373', fontSize: '14px' },
    modalFooter: { display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '16px' },
    ghostBtn: {
        background: 'transparent', border: '1px solid #555', borderRadius: '6px',
        color: '#aaa', padding: '9px 16px', fontSize: '14px', cursor: 'pointer',
    },
    confirmDeleteBtn: {
        background: 'transparent', border: '1px solid #e57373', borderRadius: '6px',
        color: '#e57373', padding: '9px 16px', fontSize: '14px', fontWeight: 600, cursor: 'pointer',
    },
    confirmReverseBtn: {
        background: 'transparent', border: '1px solid #bb86fc', borderRadius: '6px',
        color: '#bb86fc', padding: '9px 16px', fontSize: '14px', fontWeight: 600, cursor: 'pointer',
    },
};
